import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Row,
  Col,
  Image,
  ListGroup,
  Card,
  Button,
  Form,
} from "react-bootstrap";
import Rating from "../common/Rating";
import Loader from "../common/Loader";
import Message from "../common/Message";
import Meta from "../common/Meta";
import { currencyFormat } from "./../../utils/currencyFormat";
import { toTitleCase } from "./../../utils/stringFormat";
import {
  listProductDetails,
  createProductReview,
} from "../../actions/productActions";
import { PRODUCT_CREATE_REVIEW_RESET } from "../../constants/productConstants";

const ProductScreen = () => {
  const [qty, setQty] = useState(1);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  const { id } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const productDetails = useSelector((state) => state.productDetails);
  const { loading, error, product, reviews } = productDetails;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const productReviewCreate = useSelector((state) => state.productReviewCreate);
  const { success: successProductReview, error: errorProductReview } =
    productReviewCreate;

  useEffect(() => {
    if (successProductReview) {
      alert("Review Submitted!");
      setRating(0);
      setComment("");
      dispatch({ type: PRODUCT_CREATE_REVIEW_RESET });
    }
    dispatch(listProductDetails(id));
  }, [successProductReview, dispatch, id]);

  const addToBookingHandler = (e) => {
    navigate(`/booking/${id}?qty=${qty}`); // navigate to booking
  };

  const submitHandler = (e) => {
    e.preventDefault();
    dispatch(createProductReview(id, { rating, comment }));
  };

  return (
    <>
      <Link className="btn my-3 bg-primary btn-hover" to="/">
        Go Back
      </Link>
      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : (
        <>
          <Meta title={product.name} />
          <Row className="mb-5">
            <Col md={6}>
              <Image src={product.image} alt={product.name} fluid />
            </Col>
            <Col md={4} className="mt-3">
              <Card>
                <ListGroup variant="flush">
                  <ListGroup.Item variant="light">
                    <Row>
                      <Col>Price:</Col>
                      <Col>
                        <strong>{currencyFormat(product.price)}</strong>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                  <ListGroup.Item variant="light">
                    <Row>
                      <Col>Status:</Col>
                      <Col>
                        {product.countInStock > 0
                          ? "Available"
                          : "Not available"}
                      </Col>
                    </Row>
                  </ListGroup.Item>

                  {product.countInStock > 0 && (
                    <ListGroup.Item variant="light">
                      <Row>
                        <Col>Qty</Col>
                        <Col>
                          <Form.Control
                            as="select"
                            value={qty}
                            onChange={(e) => setQty(e.target.value)}
                          >
                            {[...Array(product.countInStock).keys()].map(
                              (x) => (
                                <option key={x + 1} value={x + 1}>
                                  {x + 1}
                                </option>
                              )
                            )}
                          </Form.Control>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  )}
                  <ListGroup.Item variant="light">
                    <div className="d-flex justify-content-center">
                      <Button
                        onClick={addToBookingHandler}
                        className="btn-block "
                        type="button"
                        disabled={product.countInStock === 0}
                      >
                        Add To Booking
                      </Button>
                    </div>
                  </ListGroup.Item>
                </ListGroup>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <ListGroup variant="flush">
                <ListGroup.Item variant="light">
                  <h3>{product.name}</h3>
                </ListGroup.Item>
                <ListGroup.Item variant="light">
                  <Rating
                    value={product.rating}
                    text={`${product.numReviews} reviews`}
                  />
                </ListGroup.Item>
                <ListGroup.Item variant="light">
                  {product.isDozen ? "Price per dozen: " : "Price: "}
                  {currencyFormat(product.price)}
                </ListGroup.Item>
                <ListGroup.Item variant="light">
                  Manager: {product.manager}
                </ListGroup.Item>
                <ListGroup.Item variant="light">
                  Description: {product.description}
                </ListGroup.Item>
                <ListGroup.Item variant="light">
                  Category: {toTitleCase(product.category)}
                </ListGroup.Item>
                <ListGroup.Item variant="light">
                  Type: {toTitleCase(product.type)}
                </ListGroup.Item>
                <ListGroup.Item variant="light">
                  Usage time: {product.usageTime} hrs
                </ListGroup.Item>
              </ListGroup>
            </Col>
            <Col md={6}>
              <h2>Reviews</h2>
              {reviews.length === 0 && <Message>No Reviews</Message>}

              <ListGroup variant="flush">
                {reviews.map((review) => (
                  <ListGroup.Item key={review._id} variant="light">
                    <strong>{review.name}</strong>
                    <Rating value={review.rating} />
                    <p>{review.createdAt.substring(0, 10)}</p>
                    <p>{review.comment}</p>
                  </ListGroup.Item>
                ))}
                <ListGroup.Item variant="light">
                  <h2>Write a customer Review</h2>
                  {errorProductReview && (
                    <Message variant="danger">{errorProductReview}</Message>
                  )}
                  {userInfo ? (
                    <Form onSubmit={submitHandler}>
                      <Form.Group controlId="rating">
                        <Form.Label>Rating</Form.Label>
                        <Form.Control
                          as="select"
                          value={rating}
                          onChange={(e) => setRating(e.target.value)}
                        >
                          <option value="">Select...</option>
                          <option value="1">1 - Poor</option>
                          <option value="2">2 - Fair</option>
                          <option value="3">3 - Good</option>
                          <option value="4">4 - Very Good</option>
                          <option value="5">5 - Excellent</option>
                        </Form.Control>
                      </Form.Group>
                      <Form.Group controlId="comment">
                        <Form.Label>Comment</Form.Label>
                        <Form.Control
                          as="textarea"
                          row="3"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                        ></Form.Control>
                      </Form.Group>
                      <Button type="submit" variant="primary" className="my-2">
                        Submit
                      </Button>
                    </Form>
                  ) : (
                    <Message>
                      Please <Link to="/login">sign in</Link> to write a review
                    </Message>
                  )}
                </ListGroup.Item>
              </ListGroup>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default ProductScreen;
